<template>
	<div class="gfb-root" v-show="!openPriorityElement">
		<BotChat
			v-if="showBotChat"
			controlled
			:open="activeTool === 'bot'"
			anchor="right"
			@close="closeTool"
		/>
		<NewButtons
			v-if="selectedProject"
			controlled
			:open="activeTool === 'new'"
			anchor="right"
			@close="closeTool"
		/>
		<UsersChat
			v-if="selectedProject"
			controlled
			:open="activeTool === 'chat'"
			:target-room="pendingChatTarget"
			anchor="right"
			@close="closeTool"
		/>

		<div class="gfb-stack" v-if="!activeTool">
			<transition-group v-if="expanded" name="gfb-mini-pop" tag="div" class="gfb-mini-list">
				<button
					v-for="(tool, i) in visibleTools"
					:key="tool.key"
					class="gfb-mini-fab"
					:style="{ transitionDelay: (i * 30) + 'ms' }"
					@click="openTool(tool.key)"
					:aria-label="tool.label"
				>
					<v-icon color="white" size="20">{{ tool.icon }}</v-icon>
				</button>
			</transition-group>

			<button
				v-if="visibleTools.length"
				class="gfb-master-fab"
				:class="{ 'gfb-master-fab--open': expanded }"
				@click="handleMasterClick"
				aria-label="Menú de herramientas"
			>
				<v-icon color="white" size="22">{{ masterIcon }}</v-icon>
			</button>
		</div>
	</div>
</template>

<script>
import BotChat from '@/components/Globals/Helpers/FloatingButtons/BotChat'
import NewButtons from '@/components/Globals/Helpers/FloatingButtons/NewButtons.vue'
import UsersChat from '@/components/Globals/Helpers/FloatingButtons/UsersChat.vue'
import EventBus from '@/helpers/event_bus'

export default {
	name: 'GlobalFloatingButtons',
	components: { BotChat, NewButtons, UsersChat },
	data: () => ({
		expanded: false,
		activeTool: null,
		// Sala/usuario pendiente de abrir en UsersChat, seteado cuando
		// el chat se abre desde una notificación externa (EventBus).
		pendingChatTarget: null
	}),
	computed: {
		selectedProject() {
			return this.$store.projectsStore.state.selectedProject
		},
		showBotChat() {
			return this.selectedProject?.project_config?.ai_bot_access
		},
		visibleTools() {
			const tools = []
			if (this.showBotChat) tools.push({ key: 'bot', icon: 'mdi-robot-outline', label: 'Asistente IA' })
			if (this.selectedProject) tools.push({ key: 'new', icon: 'mdi-view-grid-plus-outline', label: 'Nuevo elemento' })
			if (this.selectedProject) tools.push({ key: 'chat', icon: 'mdi-forum-outline', label: 'Chat' })
			return tools
		},
		masterIcon() {
			if (this.expanded) return 'mdi-close'
			if (this.visibleTools.length === 1) return this.visibleTools[0].icon
			return 'mdi-dots-grid'
		},
		openPriorityElement(){
			//Devuelve tru o false cuando esté abierto un elemento, tarea (task), rdi, adicional, etc.
			const task = this.$store.consultasStore.state.currentTarea
			const rdi = this.$store.rdiStore.state.currentRDI
			const adicional = this.$store.adicionalesStore.state.currentAdicional
			const entrega = this.$store.entregaStore.state.currentEntrega

			const createTask = this.$store.consultasStore.state.createTareaModal
			const createRdi = this.$store.rdiStore.state.createRDIModal
			const createAdicional = this.$store.adicionalesStore.state.createAdicionalModal
			const createEntrega = this.$store.entregaStore.state.createEntregaModal

			const createAdicionalL = this.$store.adicionalesStore.state.createPosibleAdicionalModal

			return task || rdi || adicional || entrega || createTask || createRdi || createAdicional || createEntrega || createAdicionalL
		}
	},
	watch: {
		visibleTools(newVal) {
			// Si el único tool disponible cambia (ej. deja de haber proyecto), reseteamos estado
			if (!newVal.length) {
				this.expanded = false
				this.activeTool = null
			}
		}
	},
	mounted() {
		// Permite que cualquier parte de la app abra el chat con un usuario
		// o sala específica, ej: EventBus.$emit('openChatRoom', { roomId: 123 })
		// o EventBus.$emit('openChatRoom', { userId: 45 }) si aún no hay sala creada.
		EventBus.$on('openChatRoom', this.handleOpenChatRoom)
	},
	beforeDestroy() {
		EventBus.$off('openChatRoom', this.handleOpenChatRoom)
	},
	methods: {
		handleMasterClick() {
			if (!this.visibleTools.length) return
			if (this.visibleTools.length === 1) {
				this.openTool(this.visibleTools[0].key)
				return
			}
			this.expanded = !this.expanded
		},
		openTool(key) {
			// Apertura manual: no debe arrastrar un target de una notificación anterior
			this.pendingChatTarget = null
			this.activeTool = key
			this.expanded = false
		},
		closeTool() {
			this.activeTool = null
		},
		handleOpenChatRoom(payload) {
			console.log('handleOpenChatRoom', payload)
			if (!this.selectedProject) return
			this.pendingChatTarget = payload || null
			this.activeTool = 'chat'
			this.expanded = false
		}
	}
}
</script>

<style scoped>
.gfb-root {
	position: fixed;
	bottom: 10px;
	right: 10px;
	z-index: 9998;
	font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
	pointer-events: auto !important;
}

.gfb-stack {
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: 12px;
}

.gfb-mini-list {
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: 12px;
}

/* ── Master FAB ── */
.gfb-master-fab {
	width: 50px;
	height: 50px;
	border-radius: 50%;
	background: #1C3A7A;
	border: none;
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;
	box-shadow: 0 4px 18px rgba(28, 58, 122, .45);
	transition: background .18s, transform .15s, box-shadow .18s;
	flex-shrink: 0;
}
.gfb-master-fab:hover {
	background: #2D4B95;
	transform: scale(1.06);
	box-shadow: 0 6px 24px rgba(28, 58, 122, .6);
}
.gfb-master-fab--open {
	background: #2D4B95;
}

/* ── Mini FABs ── */
.gfb-mini-fab {
	width: 44px;
	height: 44px;
	border-radius: 50%;
	background: #2D4B95;
	border: none;
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;
	box-shadow: 0 3px 14px rgba(28, 58, 122, .4);
	transition: background .15s, transform .15s, box-shadow .15s;
	flex-shrink: 0;
}
.gfb-mini-fab:hover {
	background: #1C3A7A;
	transform: scale(1.08);
	box-shadow: 0 4px 18px rgba(28, 58, 122, .5);
}

/* ── Transición mini fabs ── */
.gfb-mini-pop-enter-active,
.gfb-mini-pop-leave-active {
	transition: transform .18s cubic-bezier(.34,1.56,.64,1), opacity .18s ease;
}
.gfb-mini-pop-enter,
.gfb-mini-pop-leave-to {
	transform: scale(.4) translateY(10px);
	opacity: 0;
}

@media (max-width: 420px) {
	.gfb-root { bottom: 12px; left: 12px; right: auto; }
}
</style>